import React, { useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';

import { useFormikContext } from 'formik';
import { useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import { SortableContainer, SortableElement } from 'react-sortable-hoc';
import { arrayMoveImmutable } from 'array-move';

import Swal from 'sweetalert2';
import Button from 'react-bootstrap/Button';

import { selectStatusByName, selectSectionAddClicksByName } from '@redux/selectors/FormSelector';
import { selectSectionsPerRepeatableSection } from '@redux/selectors/ListingSelector';

import OpenHouseCard from './OpenHouseCard';

const MAX = 2;

const whiteList = [
  'OpenHouseDate',
  'OpenHouseEndTime',
  'OpenHouseStartTime',
  'OpenHouseStatus',
  'OpenHouseRemarks',
  'OpenHouseType',
  'Refreshments',
  'OpenHouseLiveURL',
];

const SortableCard = SortableElement(props => (
  <OpenHouseCard {...props} />
));

const Grid = SortableContainer(({
  fileList,
  fieldsList,
  validateFigField,
  something,
  ...otherProps
}) => (
  <div className={`grid grid-${MAX}-col`}>
    {fileList.map(([k, file], index) => (
      (!file.IsDeleted)
      && (
        <SortableCard
          key={k} // consumed by react-sortable-hoc
          index={index} // consumed by react-sortable-hoc
          sortIndex={index}
          file={file}
          validateFigField={validateFigField}
          fieldsList={fieldsList}
          {...otherProps}
        />
      )
    ))}
  </div>
));

const RepeatableOpenHouse = ({ fields, validateFigField, openHousesPayload }) => {
  const {
    values, setValues, setFieldValue,
  } = useFormikContext();

  const statusOpenHouseStatus = useSelector(state => selectStatusByName(state, 'OpenHouseStatus'));
  const statusOpenHouseDate = useSelector(state => selectStatusByName(state, 'OpenHouseDate'));
  const statusOpenHouseStartTime = useSelector(state => selectStatusByName(state, 'OpenHouseStartTime'));
  const statusOpenHouseEndTime = useSelector(state => selectStatusByName(state, 'OpenHouseEndTime'));
  const statusOpenHouseType = useSelector(state => selectStatusByName(state, 'OpenHouseType'));
  const statusOpenHouseURL = useSelector(state => selectStatusByName(state, 'OpenHouseURL'));

  const addClickCounter = useSelector(state => selectSectionAddClicksByName(state, 'OpenHouses'));
  const maxCount = useMemo(() => ((fields[0].repeatableMaxCount) ? fields[0].repeatableMaxCount : 25), [fields]);

  const sectionsPerRepeatableSection = useSelector(selectSectionsPerRepeatableSection);
  const openHousesCount = sectionsPerRepeatableSection.OpenHouses.length;

  const fileList = useMemo(() => {
    const array = [];
    if (values.OpenHouses) {
      Object.entries(values.OpenHouses).forEach(([k, file]) => {
        if (!file.IsDeleted) {
          array.push([k, file]);
        }
      });
    }
    return array;
  }, [values?.OpenHouses]);

  const fieldsList = useMemo(() => {
    const array = [];
    fields.forEach(field => {
      if (whiteList.includes(field.FieldName)) {
        array.push(field);
      }
    });
    return array;
  }, [fields]);

  const buildFormikValues = () => {
    const newValues = {};
    let index = 0;
    openHousesPayload.sort((a, b) => a.SortOrder - b.SortOrder).forEach(entry => {
      const uid = uuidv4(); // keep this an unique string that cannot be converted to a number (prevents keys reordering)
      newValues[uid] = {
        uid,
        UniqueID: entry.UniqueID,
        OpenHouseDate: entry.OpenHouseDate,
        OpenHouseEndTime: entry.OpenHouseEndTime,
        OpenHouseStartTime: entry.OpenHouseStartTime,
        OpenHouseStatus: entry.OpenHouseStatus,
        OpenHouseRemarks: entry.OpenHouseRemarks,
        OpenHouseType: entry.OpenHouseType,
        Refreshments: entry.Refreshments,
        OpenHouseLiveURL: entry.OpenHouseLiveURL,
        SortOrder: entry.SortOrder,
        IsDeleted: entry.IsDeleted,
        IsLive: entry.IsLive,
      };
      // ensure correct sort order
      if (!entry.IsDeleted) {
        newValues[uid].SortOrder = index + 1;
        index += 1;
      }
    });
    return newValues;
  };

  useEffect(() => {
    if (openHousesPayload?.length) {
      const newValues = buildFormikValues();
      setFieldValue('OpenHouses', {
        ...values.OpenHouses,
        ...newValues,
      });
    }
  }, [openHousesPayload]);

  const displayMaxCountExceedMessage = () => Swal.fire({
    position: 'center',
    title: 'Open Houses Exceeded',
    html: `The maximum number of open houses must not exceed <strong class="text-danger">${maxCount}</strong>.`,
    icon: 'error',
    iconColor: 'var(--theme-danger)',
    showConfirmButton: false,
    timer: 5000,
  });

  const addOpenHouse = () => {
    if (openHousesCount + 1 > maxCount) {
      displayMaxCountExceedMessage();
      return;
    }

    const uid = uuidv4();
    setValues({
      ...values,
      OpenHouses: {
        ...values.OpenHouses,
        [uid]: {
          uid,
          OpenHouseDate: '',
          OpenHouseEndTime: '',
          OpenHouseStartTime: '',
          OpenHouseStatus: '',
          OpenHouseRemarks: '',
          OpenHouseType: '',
          Refreshments: '',
          OpenHouseLiveURL: '',
          SortOrder: fileList.length + 1, // fileList excludes deleted entries
          IsDeleted: false,
          IsLive: false,
        },
      },
    });
  };

  // after sort ends derive new order, then set formik state
  const onSortEnd = ({ oldIndex, newIndex }) => {
    const sortedArray = arrayMoveImmutable(fileList, oldIndex, newIndex);
    let newValues = {};
    sortedArray.forEach((file, index) => {
      newValues = {
        ...newValues,
        [file[0]]: {
          ...file[1],
          SortOrder: index + 1,
        },
      };
    });
    // preserve deleted entries - excluded from fileList
    Object.entries(values.OpenHouses).forEach(file => {
      if (file[1].IsDeleted) {
        newValues = {
          ...newValues,
          [file[0]]: {
            ...file[1],
          },
        };
      }
    });
    setValues({
      ...values,
      OpenHouses: { ...newValues },
    });
  };

  useEffect(() => {
    if (addClickCounter) {
      addOpenHouse();
    }
  }, [addClickCounter]);

  return (
    <>
      { statusOpenHouseStatus?.errorMessage && <div id="OpenHouse OpenHouseStatus" className="invalid-feedback">{statusOpenHouseStatus.errorMessage}</div> }
      { statusOpenHouseDate?.errorMessage && <div id="OpenHouse OpenHouseDate" className="invalid-feedback">{statusOpenHouseDate.errorMessage}</div> }
      { statusOpenHouseStartTime?.errorMessage && <div id="OpenHouse OpenHouseStartTime" className="invalid-feedback">{statusOpenHouseStartTime.errorMessage}</div> }
      { statusOpenHouseEndTime?.errorMessage && <div id="OpenHouse OpenHouseEndTime" className="invalid-feedback">{statusOpenHouseEndTime.errorMessage}</div> }
      { statusOpenHouseType?.errorMessage && <div id="OpenHouse OpenHouseType" className="invalid-feedback">{statusOpenHouseType.errorMessage}</div> }
      { statusOpenHouseURL?.errorMessage && <div id="OpenHouse OpoenHouseURL" className="invalid-feedback">{statusOpenHouseURL.errorMessage}</div> }
      <Button
        name="btnAddOpenHouse"
        className="float-right"
        onClick={addOpenHouse}
        size="sm"
      >
        <i className="fas fa-plus mr-2" />
        Add Open House
      </Button>
      <br className="clearfix" />
      <Grid
        axis="xy"
        distance={20} // set distance to prevent click events from being swallowed
        fileList={fileList}
        fieldsList={fieldsList}
        validateFigField={validateFigField}
        onSortEnd={onSortEnd}
        useDragHandle
      />
      <br className="clearfix" />
      {(openHousesCount > MAX) && (
        <Button
          name="btnAddOpenHouse"
          className="float-right"
          onClick={addOpenHouse}
          size="sm"
        >
          <i className="fas fa-plus mr-2" />
          Add Open House
        </Button>
      )}
    </>
  );
};

RepeatableOpenHouse.propTypes = {
  fields: PropTypes.arrayOf(PropTypes.shape()),
  validateFigField: PropTypes.func,
  openHousesPayload: PropTypes.arrayOf(PropTypes.shape()),
};

export default RepeatableOpenHouse;
